import { useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import axiosServices from "@pma/utils/axios"
import {
  TASKS_STATISTICS_BY_PERIOD_ENDPOINT,
  PROJECTS_STATISTICS_ENDPOINT,
  PROJECTS_BY_CURRENT_USER_FILTER,
  TASK_BY_CURRENT_USER_ENDPOINT,
} from "@pma/utils/api-routes"
import { getStatisticsTasksChartService } from "@pma/services/task"
import type { TaskChartItem, TimeSeriesPeriod } from "./types"
import { EMPTY_PROJECTS, EMPTY_TASKS } from "@pma/utils/constants"
import { AnalyticsFilters } from "@pma/types"

export function useTasksQueries(orgId: number | undefined, filters?: AnalyticsFilters) {
  const {
    data: chartData,
    isLoading: chartLoading,
    error: chartError,
    refetch: refetchChart,
  } = useQuery<TaskChartItem[]>({
    queryKey: ["analytics-tasks-chart", orgId],
    queryFn: () => getStatisticsTasksChartService({ organization_id: orgId }),
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const {
    data: timeSeriesRaw,
    isLoading: timeSeriesLoading,
    error: timeSeriesError,
    refetch: refetchTimeSeries,
  } = useQuery<TimeSeriesPeriod[]>({
    queryKey: ["analytics-tasks-timeseries", orgId],
    queryFn: async () => {
      const response = await axiosServices.get(TASKS_STATISTICS_BY_PERIOD_ENDPOINT, {
        params: {
          period_type: 2,
          number_of_periods: 90,
          organization_id: orgId,
          status: true,
          users: true,
        },
      })
      return response.data
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const {
    data: projectStatsRaw,
    isLoading: projectStatsLoading,
    error: projectStatsError,
    refetch: refetchProjectStats,
  } = useQuery<any>({
    queryKey: ["analytics-project-stats", orgId],
    queryFn: async () => {
      const response = await axiosServices.get(PROJECTS_STATISTICS_ENDPOINT, {
        params: { organization_id: orgId },
      })
      return response.data
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const {
    data: projectsListRaw,
    isLoading: projectsListLoading,
    error: projectsListError,
    refetch: refetchProjectsList,
  } = useQuery<any[]>({
    queryKey: ["analytics-projects-list", orgId],
    queryFn: async () => {
      const response = await axiosServices.post(PROJECTS_BY_CURRENT_USER_FILTER, {
        paging_start_page: 1,
        paging_per_page: 100,
        by_organization_id: orgId,
      })
      return response.data?.items || response.data || []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const projectsList = projectsListRaw ?? EMPTY_PROJECTS

  const {
    data: tasksResponse,
    isLoading: projectsWithTasksLoading,
    error: projectsWithTasksError,
    refetch: refetchProjectsWithTasks,
  } = useQuery<{ items: any[]; itemsTotal: number }>({
    queryKey: ["analytics-all-tasks", orgId],
    queryFn: async () => {
      let allItems: any[] = []
      let page = 1
      let hasMore = true
      while (hasMore) {
        const response = await axiosServices.post(TASK_BY_CURRENT_USER_ENDPOINT, {
          organization_id: orgId,
          paging_start_page: page,
          paging_per_page: 500,
          show_deleted: false,
          without_subtask: true,
          without_task_in_groups: false,
        })
        const data = response.data
        const items = data?.items || []
        allItems = [...allItems, ...items]
        hasMore = data?.nextPage != null && items.length > 0
        page++
      }
      return { items: allItems, itemsTotal: allItems.length }
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const projectsWithTasksAllRaw = tasksResponse?.items ?? EMPTY_TASKS

  // Apply userIds filter
  const projectsWithTasksRaw = useMemo(() => {
    if (!filters?.userIds?.length) return projectsWithTasksAllRaw
    return projectsWithTasksAllRaw.filter((task: any) => {
      const users = task?.responsible_id || []
      if (!Array.isArray(users) || users.length === 0) return false
      return users.some((u: any) => {
        const uid = typeof u === "object" ? u?.id : u
        return filters.userIds.includes(uid)
      })
    })
  }, [projectsWithTasksAllRaw, filters?.userIds])

  // Apply project + date filters on top of userIds filter
  const filteredTasks = useMemo(() => {
    let tasks = projectsWithTasksRaw
    if (filters?.projectIds?.length) {
      tasks = tasks.filter((t: any) => {
        const pid = t?.project_id || t?._project?.id
        return pid && filters.projectIds.includes(pid)
      })
    }
    if (filters?.dateRange?.from) {
      tasks = tasks.filter((t: any) => new Date(t?.created_at || 0) >= filters.dateRange.from!)
    }
    if (filters?.dateRange?.to) {
      tasks = tasks.filter((t: any) => new Date(t?.created_at || 0) <= filters.dateRange.to!)
    }
    return tasks
  }, [projectsWithTasksRaw, filters?.projectIds, filters?.dateRange])

  const fetchError =
    (chartError as Error)?.message ||
    (timeSeriesError as Error)?.message ||
    (projectStatsError as Error)?.message ||
    (projectsListError as Error)?.message ||
    (projectsWithTasksError as Error)?.message ||
    null

  const refetchAll = useCallback(() => {
    refetchChart()
    refetchTimeSeries()
    refetchProjectStats()
    refetchProjectsList()
    refetchProjectsWithTasks()
  }, [refetchChart, refetchTimeSeries, refetchProjectStats, refetchProjectsList, refetchProjectsWithTasks])

  const isLoading =
    chartLoading || timeSeriesLoading || projectStatsLoading || projectsListLoading || projectsWithTasksLoading

  return {
    chartData,
    timeSeriesRaw,
    projectStatsRaw,
    projectsList,
    projectsWithTasksRaw,
    filteredTasks,
    tasksItemsTotal: projectsWithTasksRaw.length,
    fetchError,
    refetchAll,
    isLoading,
  }
}
